'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { useField, useFormikContext } from 'formik';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { FieldMessage, FieldMessageType } from '@components/form/field';
import getConfig from '@config';
export const CAPTCHA_INCOMPLETE_MESSAGE = 'Please complete the security check';

// How long a submit waits for an in-flight invisible check to produce a token
// before failing open and letting the server render the verdict.
const PENDING_TOKEN_WAIT_MS = 2000;
const PENDING_TOKEN_POLL_MS = 100;
const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

/**
 * Invisible Cloudflare Turnstile challenge wired into Formik (BHT-7146).
 *
 * Writes the challenge token into the `captchaToken` form value on success and
 * clears it on expiry or error. Tokens are single-use, so after every
 * completed submission the widget resets and issues a fresh token; a retry
 * after a validation error (for example, email already taken) never resubmits
 * a consumed token.
 *
 * Submission gating is deliberately one-sided so this component can never lock
 * a user out of signing up on its own:
 *
 * - Renders nothing when no site key is configured.
 * - When Cloudflare has escalated to a visible interactive challenge and it
 *   has not been completed, submission is blocked client-side with a field
 *   error instead of a doomed round-trip (the server rejects tokenless
 *   signups once `TURNSTILE_ENFORCED` is on).
 * - When the invisible check simply has not finished yet, submission waits
 *   briefly for the token, then fails open.
 * - When the widget itself errored (script blocked, vendor outage), it fails
 *   open immediately and the server stays the sole authority.
 *
 * The widget callbacks write the form value without triggering revalidation:
 * a full validation pass would replace the Formik errors object and silently
 * wipe server-set errors (like a rejected captcha or taken email) while the
 * user is still reading them.
 */
export const FormikTurnstile: React.FC = () => {
  const $ = _c(20);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getConfig();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const siteKey = t0.publicRuntimeConfig.turnstile.siteKey;
  const tokenRef = React.useRef(undefined);
  const isInteractiveRef = React.useRef(false);
  const didWidgetErrorRef = React.useRef(false);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = async () => {
      if (!siteKey || tokenRef.current) {
        return;
      }
      if (didWidgetErrorRef.current) {
        return;
      }
      if (isInteractiveRef.current) {
        return CAPTCHA_INCOMPLETE_MESSAGE;
      }
      const deadline = Date.now() + PENDING_TOKEN_WAIT_MS;
      while (Date.now() < deadline) {
        await sleep(PENDING_TOKEN_POLL_MS);
        if (tokenRef.current) {
          return;
        }
        if (didWidgetErrorRef.current) {
          return;
        }
        if (isInteractiveRef.current) {
          return CAPTCHA_INCOMPLETE_MESSAGE;
        }
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const validate = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      name: "captchaToken",
      validate
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [, meta, helpers] = useField(t2);
  const helpersRef = React.useRef(helpers);
  let t3;
  if ($[3] !== helpers) {
    t3 = () => {
      helpersRef.current = helpers;
    };
    $[3] = helpers;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  React.useEffect(t3);
  const {
    submitCount,
    isSubmitting
  } = useFormikContext();
  const widgetRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const lastHandledSubmit = React.useRef(0);
  let t4;
  let t5;
  if ($[5] !== isSubmitting || $[6] !== submitCount) {
    t4 = () => {
      if (!isSubmitting && submitCount > lastHandledSubmit.current) {
        lastHandledSubmit.current = submitCount;
        if (tokenRef.current) {
          tokenRef.current = undefined;
          isInteractiveRef.current = false;
          helpersRef.current.setValue(undefined, false);
          widgetRef.current?.reset();
        }
      }
    };
    t5 = [isSubmitting, submitCount];
    $[5] = isSubmitting;
    $[6] = submitCount;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  React.useEffect(t4, t5);
  const showError = !!meta.error && (meta.touched || submitCount > 0);
  let t6;
  if ($[9] !== showError) {
    t6 = () => {
      if (showError) {
        containerRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "center"
        });
      }
    };
    $[9] = showError;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== showError || $[12] !== submitCount) {
    t7 = [showError, submitCount];
    $[11] = showError;
    $[12] = submitCount;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  React.useEffect(t6, t7);
  if (!siteKey) {
    return null;
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Turnstile ref={widgetRef} siteKey={siteKey} options={{
      appearance: "interaction-only",
      size: "flexible",
      theme: "light"
    }} onSuccess={token => {
      didWidgetErrorRef.current = false;
      tokenRef.current = token;
      helpersRef.current.setValue(token, false);
      helpersRef.current.setError(undefined);
    }} onExpire={() => {
      tokenRef.current = undefined;
      helpersRef.current.setValue(undefined, false);
    }} onError={() => {
      didWidgetErrorRef.current = true;
      tokenRef.current = undefined;
      helpersRef.current.setValue(undefined, false);
    }} onBeforeInteractive={() => {
      isInteractiveRef.current = true;
    }} />;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== meta.error || $[16] !== showError) {
    t9 = showError && <FieldMessage type={FieldMessageType.ERROR} id="captchaToken-error" className="mt-1">{meta.error}</FieldMessage>;
    $[15] = meta.error;
    $[16] = showError;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9) {
    t10 = <div ref={containerRef} className="flex flex-col items-center">{t8}{t9}</div>;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
};